import React, {useRef, useEffect, useState} from 'react'
import {useCenterY, VerticalPositionContextType} from '../context/VerticalPositionContext'

interface UseElementCenterYHook {
  elementRef: React.RefObject<HTMLElement>
  centerY: number | null
}

export const useElementCenterY = (): UseElementCenterYHook => {
  const elementRef = useRef<HTMLDivElement>(null)
  const {setCenterY, setTopChildCenterY} = useCenterY()
  const [centerY, setLocalCenterY] = useState<number | null>(null) // Local state to hold centerY value

  useEffect(() => {
    const handleResize = () => {
      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect()
        const centerYValue = rect.top + rect.height / 2
        setLocalCenterY(centerYValue) // Update the local centerY state
      }
    }

    handleResize() // Initial measurement

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    // Update the context's centerY value when the local centerY state changes
    if (centerY !== null) {
      setCenterY(centerY)
    }
  }, [centerY, setCenterY])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChildCenterYChange = (event: any) => {
      const childCenterY = event.detail // Get the value from the event's detail property
      setTopChildCenterY(childCenterY)
    }

    if (elementRef.current) {
      elementRef.current.addEventListener('childCenterYChange', handleChildCenterYChange)
    }

    return () => {
      if (elementRef.current) {
        elementRef.current.removeEventListener('childCenterYChange', handleChildCenterYChange)
      }
    }
  }, [setTopChildCenterY])

  return {elementRef, centerY}
}
